import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock3,
  Info,
  KeyRound,
  Loader2,
  QrCode,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getActiveCookie, validateCookie, type CookieValidationResult } from '@/lib/api';
import { cn } from '@/lib/utils';

interface CookieStatus {
  isValid: boolean;
  expiresAt: string | null;
  lastValidated: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  hasActiveCookie: boolean;
}

type MessageState = { type: 'success' | 'error' | 'warning'; text: string } | null;
type LoginStatus = 'idle' | 'pending' | 'success' | 'expired';

const API_BASE_URL = import.meta.env.PROD
  ? '/api/cookie-proxy'
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001');

export default function SettingsPage() {
  const [isTesting, setIsTesting] = useState(false);
  const [status, setCookieStatus] = useState<CookieStatus | null>(null);
  const [validationResult, setValidationResult] = useState<CookieValidationResult | null>(null);
  const [message, setMessage] = useState<MessageState>(null);
  const [isLoadingQR, setIsLoadingQR] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [loginStatus, setLoginStatus] = useState<LoginStatus>('idle');
  const pollIntervalRef = useRef<number | null>(null);
  const expiryTimeoutRef = useRef<number | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current !== null) window.clearInterval(pollIntervalRef.current);
    if (expiryTimeoutRef.current !== null) window.clearTimeout(expiryTimeoutRef.current);
    pollIntervalRef.current = null;
    expiryTimeoutRef.current = null;
  };

  const loadCookieStatus = async () => {
    try {
      const cookie = await getActiveCookie();
      setCookieStatus(cookie ? {
        isValid: cookie.is_active,
        expiresAt: cookie.expires_at,
        lastValidated: cookie.last_validated_at,
        createdAt: cookie.created_at,
        updatedAt: cookie.updated_at,
        hasActiveCookie: true,
      } : {
        isValid: false,
        expiresAt: null,
        lastValidated: null,
        createdAt: null,
        updatedAt: null,
        hasActiveCookie: false,
      });
    } catch (loadError) {
      console.error('加载 Cookie 状态失败:', loadError);
      setMessage({ type: 'error', text: '登录状态读取失败，请稍后刷新。' });
    }
  };

  useEffect(() => {
    void loadCookieStatus();
    const statusInterval = window.setInterval(() => {
      void loadCookieStatus();
    }, 30_000);
    return () => {
      window.clearInterval(statusInterval);
      stopPolling();
    };
  }, []);

  const startPollingLoginStatus = (sessionId: string) => {
    stopPolling();
    pollIntervalRef.current = window.setInterval(async () => {
      try {
        const apiPath = API_BASE_URL.includes('/api/cookie-proxy')
          ? API_BASE_URL + '?path=api/cookie/check-login/' + sessionId
          : API_BASE_URL + '/api/cookie/check-login/' + sessionId;
        const response = await fetch(apiPath);
        const data = await response.json();

        if (data.status === 'success') {
          stopPolling();
          setLoginStatus('success');
          setMessage({ type: 'success', text: '登录成功，授权信息已自动保存。' });
          await loadCookieStatus();
          window.setTimeout(() => setQrCodeImage(null), 1600);
        } else if (data.status === 'expired' || data.status === 'error') {
          stopPolling();
          setLoginStatus('expired');
          setMessage({ type: 'error', text: '二维码已过期或登录失败，请重新获取。' });
        }
      } catch (pollError) {
        console.error('检查登录状态失败:', pollError);
        stopPolling();
        setLoginStatus('expired');
        setMessage({ type: 'error', text: '登录状态检查中断，请重新获取二维码。' });
      }
    }, 2000);

    expiryTimeoutRef.current = window.setTimeout(() => {
      stopPolling();
      setLoginStatus((current) => current === 'pending' ? 'expired' : current);
    }, 5 * 60 * 1000);
  };

  const handleStartQRLogin = async () => {
    try {
      setIsLoadingQR(true);
      setMessage(null);
      setValidationResult(null);
      setQrCodeImage(null);
      setLoginStatus('idle');
      const apiPath = API_BASE_URL.includes('/api/cookie-proxy')
        ? API_BASE_URL + '?path=api/cookie/start-qr-login'
        : API_BASE_URL + '/api/cookie/start-qr-login';
      const response = await fetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || '二维码服务返回失败');
      }
      setQrCodeImage(data.qrCodeImage);
      setLoginStatus('pending');
      startPollingLoginStatus(data.sessionId);
    } catch (startError) {
      console.error('启动二维码登录失败:', startError);
      setMessage({ type: 'error', text: '二维码服务暂时无法访问，请稍后重试。' });
    } finally {
      setIsLoadingQR(false);
    }
  };

  const handleTestCookie = async () => {
    try {
      setIsTesting(true);
      setMessage(null);
      setValidationResult(null);
      const result = await validateCookie();
      setValidationResult(result);
      setMessage({
        type: result.isValid
          ? result.validationType === 'bilibili_api' ? 'success' : 'warning'
          : 'error',
        text: result.message,
      });
      if (result.isValid) await loadCookieStatus();
    } catch (validationError) {
      console.error('Cookie 验证异常:', validationError);
      setMessage({ type: 'error', text: '验证失败，请检查网络连接或稍后重试。' });
    } finally {
      setIsTesting(false);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '未记录';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '时间异常';
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = Boolean(status?.expiresAt && new Date(status.expiresAt).getTime() <= Date.now());
  const isAuthorized = Boolean(status?.isValid && status.hasActiveCookie && !isExpired);
  const statusLabel = !status ? '读取中' : isAuthorized ? '已授权' : isExpired ? '已过期' : '未授权';
  const statusTone = !status
    ? 'text-[var(--status-unknown)]'
    : isAuthorized
      ? 'text-[var(--status-healthy)]'
      : isExpired
        ? 'text-[var(--status-warning)]'
        : 'text-[var(--status-error)]';

  return (
    <div className="console-page">
      <header className="console-page-header">
        <div>
          <p className="console-kicker">BILIBILI AUTHORIZATION</p>
          <h1 className="console-title">系统设置</h1>
          <p className="console-description">管理 B站账号授权，确保新的采集任务能够正常启动。</p>
        </div>
        <Button type="button" variant="outline" onClick={() => void handleTestCookie()} disabled={isTesting || !status?.hasActiveCookie} className="min-h-11 w-full sm:w-auto">
          {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {isTesting ? '正在验证' : '验证登录状态'}
        </Button>
      </header>

      {!isAuthorized && status && (
        <div className="console-alert-warning flex items-start gap-3 px-4 py-3" role="alert">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--status-warning)]" />
          <div>
            <p className="text-sm font-medium">{isExpired ? '当前授权已经过期' : '当前没有可用授权'}</p>
            <p className="mt-1 text-xs leading-5 text-[var(--alert-warning-subtle)]">下一场直播前请重新扫码登录并完成真实验证。</p>
          </div>
        </div>
      )}

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_372px]">
        <div className="space-y-5">
          <section className="console-panel" aria-labelledby="login-status-heading">
            <div className="console-panel-header">
              <div>
                <p className="console-kicker">AUTH STATUS · 当前授权</p>
                <h2 id="login-status-heading" className="mt-1 text-sm font-bold text-[var(--text-ink)]">登录状态</h2>
              </div>
              <div className={cn('flex items-center gap-2 text-sm font-bold', statusTone)}>
                <span className={cn(
                  'h-2 w-2 rounded-full',
                  !status ? 'bg-[var(--status-unknown)]' : isAuthorized ? 'bg-[var(--status-healthy)]' : isExpired ? 'bg-[var(--status-warning)]' : 'bg-[var(--status-error)]'
                )} />
                {statusLabel}
              </div>
            </div>

            <dl className="grid gap-px bg-[var(--border-subtle)] sm:grid-cols-3">
              <div className="bg-[var(--panel-white)] p-4">
                <dt className="text-xs text-[var(--text-muted)]">授权获取时间</dt>
                <dd className="mt-2 font-mono text-xs text-[var(--text-soft)]">{formatDateTime(status?.updatedAt || status?.createdAt || null)}</dd>
              </div>
              <div className="bg-[var(--panel-white)] p-4">
                <dt className="text-xs text-[var(--text-muted)]">最后验证</dt>
                <dd className="mt-2 font-mono text-xs text-[var(--text-soft)]">{formatDateTime(status?.lastValidated || null)}</dd>
              </div>
              <div className="bg-[var(--panel-white)] p-4">
                <dt className="text-xs text-[var(--text-muted)]">授权过期时间</dt>
                <dd className={cn('mt-2 font-mono text-xs', isExpired ? 'text-[var(--status-warning)]' : 'text-[var(--text-soft)]')}>{formatDateTime(status?.expiresAt || null)}</dd>
              </div>
            </dl>
          </section>

          <section className="console-panel" aria-labelledby="qr-login-heading">
            <div className="console-panel-header">
              <div>
                <p className="console-kicker">QR LOGIN · 扫码授权</p>
                <h2 id="qr-login-heading" className="mt-1 text-sm font-bold text-[var(--text-ink)]">使用 B站 APP 登录</h2>
              </div>
              <QrCode className="h-5 w-5 text-[var(--action-blue)]" />
            </div>

            <div className="grid gap-5 p-4 md:grid-cols-[minmax(0,1fr)_280px] md:p-[18px]">
              <div className="flex flex-col">
                <ol className="space-y-3 text-sm leading-6 text-[var(--text-soft)]">
                  {['获取一次性登录二维码', '使用 B站 APP 扫描并确认', '等待系统保存并回读授权状态'].map((step, index) => (
                    <li key={step} className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[var(--border-control)] font-mono text-[11px] text-[var(--action-blue)]">{index + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-xs leading-5 text-[var(--text-muted)]">二维码有效期 5 分钟。授权信息由后端保存，前端不会展示 Cookie 内容。</p>
                <div className="mt-5 grid gap-2 sm:grid-cols-2 md:mt-auto">
                  <Button type="button" onClick={() => void handleStartQRLogin()} disabled={isLoadingQR || loginStatus === 'pending'} className="min-h-11">
                    {isLoadingQR ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                    {isLoadingQR ? '正在获取' : loginStatus === 'pending' ? '等待扫码' : '获取登录二维码'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => void handleTestCookie()} disabled={isTesting || !status?.hasActiveCookie} className="min-h-11">
                    {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    {isTesting ? '验证中' : '真实验证'}
                  </Button>
                </div>
              </div>

              <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-[var(--border-subtle)] bg-[var(--panel-deep)] p-4">
                {qrCodeImage && loginStatus === 'pending' ? (
                  <div className="text-center">
                    <div className="rounded-md bg-white p-3">
                      <img src={qrCodeImage} alt="B站登录二维码" className="mx-auto h-52 w-52" />
                    </div>
                    <p className="mt-3 flex items-center justify-center gap-2 text-xs text-[var(--status-action)]">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />等待扫码确认
                    </p>
                  </div>
                ) : loginStatus === 'success' ? (
                  <div className="text-center">
                    <Check className="mx-auto h-9 w-9 text-[var(--status-healthy)]" />
                    <p className="mt-3 text-sm font-semibold text-[var(--status-healthy)]">登录成功</p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">授权信息已经保存</p>
                  </div>
                ) : loginStatus === 'expired' ? (
                  <div className="text-center">
                    <AlertCircle className="mx-auto h-9 w-9 text-[var(--status-warning)]" />
                    <p className="mt-3 text-sm font-semibold text-[var(--status-warning)]">二维码已失效</p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">请重新获取后再扫码</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <QrCode className="mx-auto h-10 w-10 text-[var(--border-strong)]" />
                    <p className="mt-3 text-sm text-[var(--text-soft)]">二维码将在这里显示</p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">开始后请保持页面打开</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {(isTesting || validationResult || message) && (
            <section aria-live="polite" className={cn(
              'p-4',
              isTesting
                ? 'console-panel'
                : message?.type === 'success'
                  ? 'console-alert-success'
                  : message?.type === 'warning'
                    ? 'console-alert-warning'
                    : 'console-alert-error'
            )}>
              {isTesting ? (
                <div className="flex items-start gap-3">
                  <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-[var(--status-action)]" />
                  <div><p className="text-sm font-medium text-[var(--text-ink)]">正在验证登录状态</p><p className="mt-1 text-xs text-[var(--text-muted)]">正在访问 B站用户中心，通常需要 10–20 秒。</p></div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  {message?.type === 'success' ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : message?.type === 'warning' ? <Info className="mt-0.5 h-5 w-5 shrink-0" /> : <XCircle className="mt-0.5 h-5 w-5 shrink-0" />}
                  <div className="min-w-0">
                    <p className="whitespace-pre-line text-sm leading-6">{message?.text}</p>
                    {validationResult?.details && (
                      <dl className="mt-3 space-y-1 border-t border-current/20 pt-3 text-xs opacity-85">
                        {validationResult.details.method && <div>验证方式：{validationResult.details.method}</div>}
                        {validationResult.details.validationDuration && <div>验证耗时：{validationResult.details.validationDuration}</div>}
                        {validationResult.details.cookieCount !== undefined && <div>Cookie 数量：{validationResult.details.cookieCount}</div>}
                        {validationResult.details.validatedAt && <div>验证时间：{formatDateTime(validationResult.details.validatedAt)}</div>}
                        {validationResult.details.error && <div>错误信息：{validationResult.details.error}</div>}
                        {validationResult.details.suggestion && <div>建议：{validationResult.details.suggestion}</div>}
                      </dl>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="space-y-5">
          <section className="console-panel p-[18px]">
            <p className="console-kicker">CURRENT · 当前生效授权</p>
            <div className={cn('mt-3 flex items-center gap-2 text-lg font-bold', statusTone)}>
              <span className={cn('h-2 w-2 rounded-full', isAuthorized ? 'bg-[var(--status-healthy)]' : isExpired ? 'bg-[var(--status-warning)]' : 'bg-[var(--status-error)]')} />
              {statusLabel}
            </div>
            <dl className="mt-4 space-y-3 text-xs">
              <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">数据库记录</dt><dd className="text-[var(--text-soft)]">{status?.hasActiveCookie ? '存在激活记录' : '未找到'}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">最近验证</dt><dd className="text-right font-mono text-[var(--text-soft)]">{formatDateTime(status?.lastValidated || null)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">过期时间</dt><dd className="text-right font-mono text-[var(--text-soft)]">{formatDateTime(status?.expiresAt || null)}</dd></div>
            </dl>
            <Button type="button" variant="outline" onClick={() => void handleTestCookie()} disabled={isTesting || !status?.hasActiveCookie} className="mt-4 w-full">
              <RefreshCw className={cn('h-4 w-4', isTesting && 'animate-spin')} />立即重新验证
            </Button>
          </section>

          <section className="console-panel p-[18px]">
            <p className="console-kicker">EVIDENCE · 状态含义</p>
            <div className="mt-3 space-y-3 text-xs leading-5 text-[var(--text-muted)]">
              <p className="flex gap-2"><KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-soft)]" />“存在激活记录”只代表数据库配置存在。</p>
              <p className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-soft)]" />“真实验证通过”才证明验证时可以访问 B站。</p>
              <p className="flex gap-2"><Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-soft)]" />登录状态会每 30 秒刷新一次，不能代表未来持续有效。</p>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
